/**
 * Spinner Web Component
 *
 * Displays an animated loading spinner with optional configurable text.
 *
 * Precondition: None
 * Postcondition: Custom element renders spinner with optional text label
 *
 * @attr {string} text - Optional text to display alongside spinner
 * @attr {string} size - Size variant: 'sm' | 'md' | 'lg' (default: 'md')
 */

class FtSpinner extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['text', 'size'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue && this.shadowRoot?.children.length) {
      this.render();
    }
  }

  get text(): string {
    return this.getAttribute('text') || '';
  }

  set text(value: string) {
    if (value) {
      this.setAttribute('text', value);
    } else {
      this.removeAttribute('text');
    }
  }

  get size(): 'sm' | 'md' | 'lg' {
    return (this.getAttribute('size') as 'sm' | 'md' | 'lg') || 'md';
  }

  set size(value: 'sm' | 'md' | 'lg') {
    this.setAttribute('size', value);
  }

  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-family: var(--font-sans, Inter, system-ui, sans-serif);
        color: var(--muted, #555555);
      }

      @media (prefers-color-scheme: dark) {
        :host {
          color: var(--muted, #a3a3a3);
        }
      }

      .spinner {
        border-radius: 50%;
        border-style: solid;
        border-color: var(--card-border, #e2e4e8);
        border-top-color: var(--text, #1c1c1e);
        will-change: transform;
        animation: spin 1000ms ease-in-out infinite;
      }

      @media (prefers-color-scheme: dark) {
        .spinner {
          border-color: var(--card-border, #24262c);
          border-top-color: var(--text, #ffffff);
        }
      }

      /* Size variants */
      :host([size="sm"]) .spinner {
        width: 12px;
        height: 12px;
        border-width: 1.5px;
      }

      :host([size="sm"]) .text {
        font-size: 12px;
      }

      :host(:not([size])) .spinner,
      :host([size="md"]) .spinner {
        width: 16px;
        height: 16px;
        border-width: 2px;
      }

      :host(:not([size])) .text,
      :host([size="md"]) .text {
        font-size: 13px;
      }

      :host([size="lg"]) .spinner {
        width: 24px;
        height: 24px;
        border-width: 3px;
      }

      :host([size="lg"]) .text {
        font-size: 14px;
      }

      .text {
        line-height: 1.5;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @media (prefers-reduced-motion: reduce) {
        .spinner {
          animation: none;
          border-top-color: var(--primary, #e0524d);
        }
      }
    `;
  }

  private getTemplate(): string {
    const text = this.text;
    return `
      <div class="spinner" role="status" aria-label="${text || 'Loading'}"></div>
      ${text ? `<span class="text">${text}</span>` : ''}
    `;
  }
}

if (!customElements.get('ft-spinner')) {
  customElements.define('ft-spinner', FtSpinner);
}
